import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import { Input } from "@/shared/components/ui/input";
import { SafeText } from "@/shared/components/SafeText";
import { TestPartitionBadge } from "@/shared/components/TestPartitionBadge";
import { useDebouncedValue } from "@/shared/hooks/use-debounced-value";
import { sanitizeDisplayText } from "@/shared/lib/safe-text";
import { shortId } from "./location-label";
import { useOrganizationSearch, type OrganizationOption } from "./use-organization-search";

const SEARCH_DEBOUNCE_MS = 300;

interface OrganizationPickerProps {
  readonly selected: OrganizationOption | null;
  readonly onSelect: (organization: OrganizationOption) => void;
  readonly onClear: () => void;
  readonly disabled?: boolean;
}

/**
 * Name and kind, plus what tells two same-named organizations apart (names are
 * not unique and ADMIN is a one-click grant): the owner, the test partition
 * and the tail of the id.
 */
function OrganizationSummary({ organization }: { readonly organization: OrganizationOption }) {
  const { t } = useTranslation();
  return (
    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
      <span className="flex min-w-0 items-center gap-2">
        <span className="min-w-0 flex-1 truncate font-medium">
          <SafeText fallback={organization.id}>{organization.name}</SafeText>
        </span>
        <TestPartitionBadge
          isTest={organization.isTest}
          testId={`org-picker-test-${organization.id}`}
        />
        <Badge variant="outline">{t(`organizationAccess.kinds.${organization.kind}`)}</Badge>
      </span>
      <span className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
        <span className="min-w-0 flex-1 truncate">
          {t("organizationAccess.picker.owner", {
            name: sanitizeDisplayText(organization.owner.displayName) || organization.owner.id,
          })}
        </span>
        <span className="shrink-0 font-mono">{shortId(organization.id)}</span>
      </span>
    </span>
  );
}

/**
 * Searchable single choice of an organization. Shows each match's name and
 * kind so the operator can tell a venue organization from a property one.
 */
export function OrganizationPicker({
  selected,
  onSelect,
  onClear,
  disabled = false,
}: OrganizationPickerProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const debounced = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const { organizations, isSearchable, isLoading, isError } = useOrganizationSearch(debounced);

  if (selected) {
    return (
      <div
        data-testid="org-picker-selected"
        className="flex min-w-0 items-center gap-2 rounded-md border border-border bg-muted/40 p-3 text-sm"
      >
        <OrganizationSummary organization={selected} />
        <Button
          data-testid="org-picker-clear"
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={onClear}
        >
          {t("organizationAccess.add.change")}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Input
        data-testid="org-picker-search"
        type="search"
        value={search}
        disabled={disabled}
        placeholder={t("organizationAccess.add.searchPlaceholder")}
        aria-label={t("organizationAccess.add.organizationLabel")}
        onChange={(event) => setSearch(event.target.value)}
      />
      {search.trim().length === 0 || !isSearchable ? (
        <p data-testid="org-picker-hint" className="text-sm text-muted-foreground">
          {t("organizationAccess.add.searchHint")}
        </p>
      ) : isLoading ? (
        <p data-testid="org-picker-loading" className="text-sm text-muted-foreground">
          {t("organizationAccess.add.searching")}
        </p>
      ) : isError ? (
        <p data-testid="org-picker-error" role="alert" className="text-sm text-destructive">
          {t("organizationAccess.add.searchError")}
        </p>
      ) : organizations.length === 0 ? (
        <p data-testid="org-picker-empty" className="text-sm text-muted-foreground">
          {t("organizationAccess.add.noResults")}
        </p>
      ) : (
        <ul className="max-h-56 divide-y divide-border overflow-y-auto rounded-md border border-border">
          {organizations.map((organization) => (
            <li key={organization.id}>
              <button
                type="button"
                data-testid={`org-picker-option-${organization.id}`}
                disabled={disabled}
                onClick={() => onSelect(organization)}
                className="flex w-full min-w-0 items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted focus:bg-muted focus:outline-none"
              >
                <OrganizationSummary organization={organization} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
